/* path: frontend/src/pages/HistoryPage.tsx */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  deleteDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { db, auth } from "../lib/firebase";
import toast from "react-hot-toast";
import {
  X,
  Trash2,
  ExternalLink,
  Link as LinkIcon,
  Download,
} from "lucide-react";
import { API_BASE_URL } from "@/config";

type FsTs =
  | Timestamp
  | { seconds: number; nanoseconds?: number }
  | null
  | undefined;

interface QuizDoc {
  id: string;
  title?: string;
  video_url: string;
  createdAt: FsTs;
  questions?: Array<{ question: string }>;
}

function tsToDate(ts: FsTs): Date | null {
  if (!ts) return null;
  if (typeof (ts as any).toDate === "function")
    return (ts as any as Timestamp).toDate();
  if (typeof (ts as any).seconds === "number")
    return new Date((ts as any).seconds * 1000);
  return null;
}

export default function HistoryPage() {
  const { user } = useAuth();
  const [quizzes, setQuizzes] = useState<QuizDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [openVid, setOpenVid] = useState<QuizDoc | null>(null);

  // Subscribe to user's quizzes
  useEffect(() => {
    if (!user?.uid) {
      setQuizzes([]);
      setLoading(false);
      return;
    }
    setErr(null);
    setLoading(true);

    const qRef = query(
      collection(db, `users/${user.uid}/quizzes`),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as any),
        })) as QuizDoc[];
        setQuizzes(list);
        setLoading(false);
      },
      (e) => {
        setErr(e?.message || "Failed to load history.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user?.uid]);

  const handleDelete = useCallback(
    async (q: QuizDoc) => {
      if (!user?.uid || !q?.id) return;

      const ok = window.confirm(
        `Delete this quiz?\n\n"${q.title || "Untitled"}"\n\nThis cannot be undone.`
      );
      if (!ok) return;

      try {
        // 🔒 FIXED: Add authentication to delete-video endpoint
        try {
          const currentUser = auth?.currentUser;
          const headers: HeadersInit = {
            "Content-Type": "application/json",
          };

          if (currentUser) {
            const token = await currentUser.getIdToken();
            headers["Authorization"] = `Bearer ${token}`;
          }

          await fetch(`${API_BASE_URL}/api/video/delete-video`, {
            method: "POST",
            headers,
            body: JSON.stringify({ url: q.video_url }),
          });
        } catch (deleteError) {
          // Log but don't block - best effort cleanup
          console.warn("Video cleanup failed:", deleteError);
        }

        // Delete from Firestore
        await deleteDoc(doc(db, `users/${user.uid}/quizzes/${q.id}`));
        toast.success("Deleted.");
        if (openVid?.id === q.id) setOpenVid(null);
      } catch (e: any) {
        console.error(e);
        toast.error(e?.message || "Failed to delete.");
      }
    },
    [user?.uid, openVid?.id]
  );

  const grid = useMemo(() => {
    if (loading) {
      return (
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="overflow-hidden rounded-lg border border-black/5 dark:border-white/10 bg-white dark:bg-gray-800 shadow"
            >
              <div className="aspect-video bg-slate-200 dark:bg-slate-700 animate-pulse" />
              <div className="p-3">
                <div className="h-4 w-2/3 bg-slate-200 dark:bg-slate-700 rounded mb-2 animate-pulse" />
                <div className="h-3 w-1/3 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      );
    }
    if (err) {
      return (
        <p className="rounded-lg border border-red-200 dark:border-red-900/40 bg-white dark:bg-gray-800 p-3 text-red-700 dark:text-red-300">
          {err}
        </p>
      );
    }
    if (quizzes.length === 0) {
      return (
        <p className="text-slate-600 dark:text-slate-400">
          No quizzes yet. Generate one on the Home page!
        </p>
      );
    }

    return (
      <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
        {quizzes.map((q) => {
          const d = tsToDate(q.createdAt);
          const title =
            q.title ||
            (q.questions && q.questions[0]?.question
              ? q.questions[0].question.slice(0, 80)
              : "Untitled Quiz");

          return (
            <div
              key={q.id}
              className="relative overflow-hidden rounded-lg border border-black/5 dark:border-white/10 bg-white dark:bg-gray-800 shadow group"
            >
              <button
                className="w-full text-left"
                onClick={() => setOpenVid(q)}
                title="Open preview"
                aria-label={`Open preview for ${title}`}
              >
                <div className="relative w-full aspect-video bg-slate-200 dark:bg-slate-700">
                  <video
                    className="w-full h-full object-cover"
                    src={q.video_url}
                    muted
                    playsInline
                    preload="metadata"
                    onError={(e) => {
                      const target = e.target as HTMLVideoElement;
                      target.style.display = 'none';
                      const fallback = target.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                  <div
                    className="absolute inset-0 flex-col items-center justify-center bg-slate-100 dark:bg-slate-800 text-center p-4"
                    style={{ display: 'none' }}
                  >
                    <div className="text-2xl mb-2">🎬</div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">Video expired</p>
                    <button
                      className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.location.href = `/renders/${q.id}/edit`;
                      }}
                    >
                      Re-render
                    </button>
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="font-semibold mb-1 line-clamp-2">{title}</h3>
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    {d ? d.toLocaleString() : "—"}
                  </p>
                </div>
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  void handleDelete(q);
                }}
                className="absolute top-2 right-2 inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/90 dark:bg-slate-900/90 text-slate-600 dark:text-slate-300 hover:text-red-600 hover:bg-white dark:hover:bg-slate-800 shadow transition-colors"
                title="Delete quiz"
                aria-label={`Delete ${title}`}
              >
                <Trash2 size={18} />
              </button>
            </div>
          );
        })}
      </div>
    );
  }, [loading, err, quizzes, handleDelete]);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenVid(null);
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  return (
    <div className="max-w-5xl mx-auto p-6 text-slate-900 dark:text-slate-100">
      <h2 className="text-2xl font-bold mb-4">Your Quizzes</h2>
      {grid}

      {openVid && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setOpenVid(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div
            className="relative w-full max-w-3xl rounded-lg border border-black/5 dark:border-white/10 bg-white/95 dark:bg-gray-900/95 shadow-lg p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-2 right-2 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
              onClick={() => setOpenVid(null)}
              aria-label="Close modal"
            >
              <X size={20} />
            </button>

            <h3
              id="modal-title"
              className="text-lg font-semibold mb-3 line-clamp-2 pr-8"
            >
              {openVid.title ||
                (openVid.questions && openVid.questions[0]?.question) ||
                "Preview"}
            </h3>

            <video
              src={openVid.video_url}
              controls
              className="w-full rounded mb-3"
              preload="auto"
              onError={() => {
                toast.error("Failed to load video");
              }}
            />

            <div className="flex flex-wrap gap-2">

              href={openVid.video_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center px-2.5 py-1.5 rounded border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              title="Open in new tab"
              aria-label="Open video in new tab"
              <a>
                <ExternalLink size={16} />
                <span className="ml-1">Open</span>
              </a>
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(openVid.video_url);
                    toast.success("URL copied");
                  } catch {
                    toast.error("Failed to copy URL");
                  }
                }}
                className="inline-flex items-center px-2.5 py-1.5 rounded border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                title="Copy URL"
                aria-label="Copy video URL to clipboard"
              >
                <LinkIcon size={16} />
                <span className="ml-1">Copy</span>
              </button>

              href={openVid.video_url}
              download
              className="inline-flex items-center px-2.5 py-1.5 rounded border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              title="Download"
              aria-label="Download video"
              <a>
                <Download size={16} />
                <span className="ml-1">Download</span>
              </a>
              <button
                onClick={() => void handleDelete(openVid)}
                className="ml-auto inline-flex items-center px-2.5 py-1.5 rounded border border-black/10 dark:border-white/10 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                title="Delete"
                aria-label="Delete this quiz"
              >
                <Trash2 size={16} />
                <span className="ml-1">Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}